import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Mail, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVerifiedDomains, EmailDomain } from '@/hooks/useVerifiedDomains';

interface DomainSelectorProps {
  organisationId: string | null;
  selectedDomainId: string;
  emailPrefix: string;
  senderName: string;
  onDomainChange: (domainId: string) => void;
  onEmailPrefixChange: (prefix: string) => void;
  onSenderNameChange: (name: string) => void;
  disabled?: boolean;
}

/**
 * DomainSelector Component
 *
 * Allows user to:
 * 1. Select a verified domain from dropdown
 * 2. Enter email prefix (e.g., "info", "promotions")
 * 3. Enter sender name (auto-populated from domain default)
 *
 * Displays warning if no verified domains exist.
 */
export const DomainSelector: React.FC<DomainSelectorProps> = ({
  organisationId,
  selectedDomainId,
  emailPrefix,
  senderName,
  onDomainChange,
  onEmailPrefixChange,
  onSenderNameChange,
  disabled = false,
}) => {
  const { domains, loading, error, refresh, hasVerifiedDomains } = useVerifiedDomains(organisationId);

  // Track the last selected domain to detect domain changes
  const [lastSelectedDomainId, setLastSelectedDomainId] = React.useState<string>('');

  // Get selected domain object
  const selectedDomain = domains.find((d) => d.id === selectedDomainId);

  // Auto-populate email prefix when domain changes (not when prefix is cleared)
  React.useEffect(() => {
    if (selectedDomain && selectedDomainId !== lastSelectedDomainId) {
      setLastSelectedDomainId(selectedDomainId);

      // Extract prefix from default_from_email (e.g., "info@domain.com" -> "info")
      const defaultEmail = selectedDomain.default_from_email;
      if (defaultEmail && defaultEmail.includes('@')) {
        const extractedPrefix = defaultEmail.split('@')[0];
        onEmailPrefixChange(extractedPrefix);
      }
    }
  }, [selectedDomain, selectedDomainId, lastSelectedDomainId, onEmailPrefixChange]);

  // Auto-populate sender name when domain is selected
  React.useEffect(() => {
    if (selectedDomain && !senderName) {
      onSenderNameChange(selectedDomain.default_from_name || '');
    }
  }, [selectedDomain, senderName, onSenderNameChange]);

  // Construct full sender email
  const fullSenderEmail = selectedDomain && emailPrefix
    ? `${emailPrefix}@${selectedDomain.domain}`
    : '';

  return (
    <div className="space-y-4">
      {/* Domain Selection */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="domain-select" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Sender Domain *
          </Label>
          {!loading && hasVerifiedDomains && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={refresh}
              disabled={loading}
              className="h-7 px-2"
            >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading verified domains...
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : !hasVerifiedDomains ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4 stroke-white" />
            <AlertDescription className="text-white">
              <div className="space-y-2">
                <p className="font-medium text-white">No verified domains found</p>
                <p className="text-sm text-white">
                  You must add and verify a domain before sending email campaigns.
                  Contact us at support@pumpd.co.nz to set up your domain for sending emails.
                </p>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Select
              value={selectedDomainId}
              onValueChange={onDomainChange}
              disabled={disabled || !hasVerifiedDomains}
            >
              <SelectTrigger id="domain-select" className="w-full">
                <SelectValue placeholder="Select a verified domain..." />
              </SelectTrigger>
              <SelectContent>
                {domains.map((domain) => (
                  <SelectItem key={domain.id} value={domain.id}>
                    <div className="flex items-center gap-2">
                      <span>{domain.domain}</span>
                      <Badge className="text-xs bg-brand-green text-white border-0">
                        Verified
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDomain && (
              <div className="text-xs text-muted-foreground">
                Default: {selectedDomain.default_from_email}
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Prefix Input */}
      {selectedDomain && (
        <div className="space-y-2">
          <Label htmlFor="email-prefix">
            Email Prefix * <span className="text-muted-foreground font-normal">(e.g., info, promotions, hello)</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="email-prefix"
              type="text"
              placeholder="info"
              value={emailPrefix}
              onChange={(e) => onEmailPrefixChange(e.target.value.toLowerCase().replace(/[^a-z0-9-_.]/g, ''))}
              disabled={disabled}
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground">@{selectedDomain.domain}</span>
          </div>
          {fullSenderEmail && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{fullSenderEmail}</span>
            </div>
          )}
        </div>
      )}

      {/* Sender Name Input */}
      {selectedDomain && (
        <div className="space-y-2">
          <Label htmlFor="sender-name">
            Sender Name * <span className="text-muted-foreground font-normal">(appears in "From" field)</span>
          </Label>
          <Input
            id="sender-name"
            type="text"
            placeholder="Your Restaurant Name"
            value={senderName}
            onChange={(e) => onSenderNameChange(e.target.value)}
            disabled={disabled}
          />
          {senderName && fullSenderEmail && (
            <div className="text-xs text-muted-foreground">
              Will appear as: <span className="font-medium">{senderName} &lt;{fullSenderEmail}&gt;</span>
            </div>
          )}
        </div>
      )}

      {/* Validation Warning */}
      {!selectedDomainId && hasVerifiedDomains && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Please select a domain to continue. This is required for sending emails.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
